import { useState, useContext, useEffect } from 'react';
import { TeamsFxContext } from '../context';
import { Button, MessageBar } from '@fluentui/react-components';
import { useGraph } from '../hooks';
import { ClientTypeError, ILaunchUrl } from '../interfaces';
import { getLaunchUrls } from '../services';
import { Loader } from './Loader';
import { DebugInfo } from './DebugInfo';
import { useTeams } from '@microsoft/teamsfx-react';

export default function Launch() {
	const { themeString } = useContext(TeamsFxContext);
	const [teamsContext] = useTeams();
	const [showDebug, enableDebug] = useState<boolean>(false);
	const [initialized, setInitialized] = useState<boolean>(false);
	const [errors, setErrors] = useState<string>();

	// Setup url redirector
	const [url, setUrl] = useState<ILaunchUrl>(getLaunchUrls(themeString));
	const callbackUrl = () => setUrl({ ...url, authenticated: true });

	// Authenticate
	const graph = useGraph(callbackUrl);

	// Init complete when we're done trying to login or have errored
	useEffect(() => {
		if ((!graph.loading && url.authenticated) || (!graph.loading && errors)) {
			setInitialized(true);
		}
	}, [errors, graph.data?.error, graph.loading, url.authenticated]);

	// Capture all types of errors into one simple message
	useEffect(() => {
		if (graph.error) {
			setErrors((graph.error as any)?.body ?? (graph.error as any)?.message ?? JSON.stringify(graph.error));
		}
		// If teamsContext.context updated trigger reload on Client type error
		else if (graph.data?.error instanceof ClientTypeError) {
			if (!graph.loading) {
				graph.reload();
			}
			return;
		}
		// Display any other errors
		else if (graph.data?.error) {
			setErrors(graph.data.error.message ?? graph.data.error.body ?? graph.data.error.code);
		}
	}, [graph, graph.error, graph.data?.error, teamsContext.context]);

	const openInBrowser = () => {
		if (url?.webUrl) {
			window.open(url.webUrl);
			// This may not be required, TODO: test
			setTimeout(() => setUrl({ ...url, authenticated: true }), 5000);
		}
	};

	const openAnyway = () => {
		setInitialized(true);
		callbackUrl();
		setErrors(undefined);
	};

	// Log debug once
	if (initialized) {
		console.debug('launchUrls', url);
		console.debug('teamsContext', teamsContext);
		console.debug(
			'useGraph',
			JSON.stringify({ loading: graph.loading, dataError: graph.data?.error, error: graph.error })
		);
	}

	// Show error on missconfiguration
	if (!url?.appUrl) {
		return <MessageBar intent='error'>Configuration error: Launch app URL not found.</MessageBar>;
	}

	return (
		<div className={themeString === 'default' ? 'light' : themeString === 'dark' ? 'dark' : 'contrast'}>
			<div className='launch page'>
				<div className='narrow'>
					<div className='page-centered'>
						{/* Loader */}
						{!initialized && <Loader />}

						{/* Successfully authenticated and ready to redirect to env url */}
						{initialized && url.authenticated && !errors && (
							<>
								<Loader />
								<meta httpEquiv='Refresh' content={`0; URL=${getLaunchUrls(themeString).appUrl}`} />
							</>
						)}

						{/* Authentication failed show sign-in button and errors */}
						{initialized && errors && (
							<>
								<div hidden={graph.loading} className='card-auth'>
									<h1>Authentication</h1>
									<div className='section-margin'>
										<p>
											Click sign-in to authenticate and grant permission to the TPG
											ProjectPowerPack Teams App.{' '}
											<span className='debug-button' onClick={() => enableDebug(!showDebug)}>
												°
											</span>
										</p>

										<div className='btn-auth-group'>
											<Button
												appearance='primary'
												disabled={graph.loading}
												onClick={graph.reload}
												size='large'
											>
												Sign-in
											</Button>

											<Button
												appearance='secondary'
												disabled={graph.loading}
												onClick={openInBrowser}
												size='large'
											>
												Open in browser
											</Button>

											<Button
												appearance='outline'
												disabled={graph.loading}
												onClick={openAnyway}
												size='large'
											>
												Try again
											</Button>
										</div>
									</div>

									{/* Just for debug enable on click of ? in text*/}
									{showDebug && <DebugInfo graph={graph} environments={graph.data?.environments} />}
								</div>

								{/* Show errors in bottom msg bar */}
								{errors ? (
									<div className='error-floating'>
										<MessageBar intent='warning'>Oops! {errors} &nbsp;&nbsp;</MessageBar>
									</div>
								) : (
									<></>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
